import { useSyncExternalStore } from 'react'

/**
 * Singleton midnight clock — one timer shared by all subscribers.
 * At midnight, bumps a counter and notifies all components at once.
 */
let dateKey = new Date().toDateString()
let timer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

function schedule() {
  if (timer) clearTimeout(timer)
  const now = new Date()
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1)
  timer = setTimeout(() => {
    dateKey = new Date().toDateString()
    listeners.forEach(fn => fn())
    schedule()
  }, midnight.getTime() - now.getTime())
}

function subscribe(callback: () => void) {
  if (listeners.size === 0) schedule()
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
    if (listeners.size === 0 && timer) {
      clearTimeout(timer)
      timer = null
    }
  }
}

function getSnapshot() {
  return dateKey
}

/**
 * Forces a re-render at midnight so date-relative labels
 * (e.g. "Morgen" → "Heute") update automatically.
 *
 * Uses a single shared timer regardless of how many components subscribe.
 */
export function useMidnightRefresh(): string {
  return useSyncExternalStore(subscribe, getSnapshot)
}
